<template>
  <DefaultPageContainer
    :cnc-ads-page="cncAdsPage"
    hide-breadcrumbs
  >

    <!-- 1st ROW - FIRST 3 ARTICLES -->
    <section class="section">
      <!-- TOP 3 ARTICLES -->
      <DefaultTwoColumnsContent left-container-class="section__left-col--row">
        <template #left>
          <DefaultArticlesArticleCard
            v-if="!debugStore.isArticlesLoading"
            v-for="(article, index) in articleStore.getOnlyFilteredArticles(0, 1)"
            :key="`article_${article.id}`"
            :article="article"
            :data-index="index"
            heading-element="h1"
            :type="ArticleComponentType.MAIN"
          />
          <!-- SKELETONS - LOADING 1st article -->
          <div class="w-full" v-if="debugStore.isArticlesLoading || (!articleStore.getFilteredArticlesCount() && articleStore.isLoading)">
            <DefaultSkeletonsArticleCard
              :type="ArticleComponentType.MAIN"
            />
          </div>
        </template>
        <template #right>
          <div class="flex-container">
            <DefaultArticlesArticleCard
              v-if="!debugStore.isArticlesLoading"
              v-for="(article, index) in articleStore.getOnlyFilteredArticles(1, mainArticlesCount - 1)"
              :key="`article_${article.id}`"
              :article="article"
              :data-index="index"
              :type="ArticleComponentType.SIDE"
            />
            <!-- SKELETONS - LOADING 2nd and 3rd article -->
            <DefaultSkeletonsArticleCard
              v-if="debugStore.isArticlesLoading || articleStore.getFilteredArticlesCount() < 2 "
              :type="ArticleComponentType.SIDE"
            />
            <DefaultSkeletonsArticleCard
              v-if="debugStore.isArticlesLoading || articleStore.getFilteredArticlesCount() < 3"
              :type="ArticleComponentType.SIDE"
            />
          </div>
        </template>
      </DefaultTwoColumnsContent>

      <!-- 2nd ROW - NEXT ARTICLES -->
      <DefaultTwoColumnsContent
        right-container-class="section__right-col--mobile-hide"
      >
        <template #left>
          <!-- NO ARTICLES -->
          <DefaultErrorsNoResultsFound
            v-if="!articleStore.getFilteredArticlesCount() && !articleStore.isLoading"
          />

          <!-- 4th and next article -->
          <div class="section__articles-container">

            <!-- stories -->
            <DefaultStories
              v-if="storiesWrapper.isEnabled()"
              :iframe-src="storiesWrapper.getIframeSrc()"
            />

            <!-- article list -->
            <DefaultArticlesListing
              v-if="!debugStore.isArticlesLoading"
              :articles="articleStore.getOnlyFilteredArticles(mainArticlesCount, (page * pageArticlesCount) - mainArticlesCount)"
              :ad-page="cncAdsPage"
            />

            <!-- SKELETONS - Loading 4th and next articles -->
            <div
              v-if="debugStore.isArticlesLoading || articleStore.isLoading"
              v-for="item in 10"
              :key="`skeleton_${item}`"
              class="article-wrapper"
            >
              <DefaultSkeletonsArticleCard />
            </div>

            <!-- Next page Button -->
            <div
              v-if="articleStore.getArticlesCount() >= articleStore.limit"
              class="button-more-container"
            >
              <DefaultArticlesButtonMore
                :label="$t('articles.button_more')"
                :disabled="articleStore.isLoading"
                @click="nextPage"
              />
            </div>

            <!-- External articles (články z jiných titulů) -->
            <DefaultExternalArticles
              :title="$t('articles.external_articles_title')"
              :articles-count="pageRecommendArticlesCount"
            />

          </div>
        </template>
        <template #right>
          <!-- CNC Ads - right sticky container -->
          <div class="advertisement-right-col h-full">
            <div class="sticky-sidebar-ad-wrapper">
              <DefaultAdsCncWrapper
                :position="UniFeAdsPosition.COLUMN_STICKY"
                :page="cncAdsPage"
              />
            </div>
          </div>
        </template>
      </DefaultTwoColumnsContent>
    </section>
  </DefaultPageContainer>
</template>

<script setup lang="ts">
import {ARTICLE_QUERY_TYPE, useArticlesStore} from "~/stores/useArticlesStore"
import {ArticleStoreType} from "~/libs/constants/ArticleStoreType"
import ArticleComponentType from "~/libs/constants/ArticleComponentType"
import {useCncAdsWrapper} from "~/composables/useCncAdsWrapper"
import {CncAdsPage} from "~/libs/constants/CncAdsPage"
import {UniFeAdsPosition} from "~/libs/constants/UniFeAdsPosition"
import {useSeoWrapper} from "~/composables/useSeoWrapper"
import {useDebugStore} from "~/stores/useDebugStore";

// i18n routing
definePageMeta({
  name: 'homepage',
})

// Set SEO meta
useSeoWrapper().setSeoMeta()

const debugStore = useDebugStore()
const articleStore = useArticlesStore(ArticleStoreType.HOMEPAGE)
const config = useRuntimeConfig()
const route = useRoute()
const router = useRouter()
const storiesWrapper = useStoriesWrapper()
const cncAdsPage = CncAdsPage.HOMEPAGE

const mainArticlesCount = 3

/**
 * Articles
 */
const pageArticlesCount = Number(config.public.homepagePageArticlesCount || 17)
const pageSsrArticlesCount = Number(config.public.homepageSsrArticlesCount || 6)
let page = ref(Number(route.query.page || 1))

/**
 * Recommended articles
 */
const pageRecommendArticlesCount = Number(config.public.homepageRecommendArticlesCount || 5)

/**
 * Load next page - articles
 */
const nextPage = async () => {
  page.value = page.value + 1
  router.push({
    query: {
      ...route.query,
      page: page.value
    }
  })
  articleStore.setLimit(page.value * pageArticlesCount)
  try {
    await articleStore.fetchArticles(undefined, ARTICLE_QUERY_TYPE.PREVIEW)
    articleStore.fetchDetailsForArticles(ARTICLE_QUERY_TYPE.WITH_AUTHOR)
  } catch (e) {
    // TODO - show message to customer?
  }
}

if (import.meta.server) {
  try {
    // sever articles preload - preload pageSsrArticlesCount articles on server
    articleStore.setLimit(pageSsrArticlesCount)
    await articleStore.fetchArticles(undefined, ARTICLE_QUERY_TYPE.PREVIEW)
  } catch (e) {
    // TODO - show message to customer?
  }
}

/**
 * On mounted - load data client
 */
onMounted(async () => {
  // client side data load
  try {
    articleStore.setLimit(page.value * pageArticlesCount)
    await articleStore.fetchArticles(undefined, ARTICLE_QUERY_TYPE.PREVIEW)
    await articleStore.fetchDetailsForArticles(ARTICLE_QUERY_TYPE.WITH_AUTHOR)
  } catch (e) {
    // TODO - show message to customer?
  }

  /**
   * CncAds Page change
   */
  useCncAdsWrapper().changePageAds(cncAdsPage, {
    route: route
  })

})
</script>

